import { useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from '../../../ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../../../ui/avatar';
import { MapPin } from 'lucide-react';
import { updateJobStatus } from '../../../../apiManager/shoveller/matchJobApi';
import { Loader } from "../../../base/Loader";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../../ui/dialog';
import { Button } from '../../../ui/button';
import { JobInformation } from "../../homeowner/jobs/detail/JobInformation";
import { ArrowLeft, CheckCircle } from 'lucide-react';

export function JobItem({ job }) {
  const { userInfo } = useSelector((state) => state.auth);
  const navigate = useNavigate();
  
  const [showJobDetails, setShowJobDetails] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371;
    const dLat = (lat2 - lat1) * (Math.PI / 180);
    const dLon = (lon2 - lon1) * (Math.PI / 180);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * (Math.PI / 180)) *
      Math.cos(lat2 * (Math.PI / 180)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c;
    return distance.toFixed(2);
  };

  const handleAcceptJob = async () => {
    try {
      setLoading(true);
      const res = await updateJobStatus(job._id, userInfo.user.id, true);
      if (res.error) {
        setError(res.error);
        return;
      }
      setShowJobDetails(false);
      navigate('/shoveller/searchJobByList');
    } catch (error) {
      setError(error.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setShowJobDetails(false);
  };

  return (
    <>
      <Card
        className="hover:shadow-lg transition-all duration-300 cursor-pointer"
        onClick={() => setShowJobDetails(true)}
      >
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Avatar className="w-12 h-12">
                <AvatarImage src={job.houseOwnerId.imageUrl} />
                <AvatarFallback className="bg-black text-white">
                  {job.houseOwnerId.userName?.charAt(0) || 'H'}
                </AvatarFallback>
              </Avatar>
              <div className="space-y-1">
                <h3 className="text-xl font-semibold">{job.houseOwnerId.userName}</h3>
                <p className="text-lg text-gray-700">{job.serviceDetail.service}</p>
                <p className="text-base text-gray-500 flex items-center gap-1">
                  <MapPin className="w-4 h-4" />
                  {calculateDistance(
                    userInfo.user.latitude,
                    userInfo.user.longitude,
                    job.location.coordinates[1],
                    job.location.coordinates[0]
                  )} km away
                </p>
              </div>
            </div>
            <div className="text-right space-y-2">
              <div className="text-2xl font-bold text-green-600">
                {job.isRequestedForVolunteer ? "$0" : `$${(job.paymentInfo.amount * 0.008).toFixed(1)}`}
              </div>
              {job.paymentInfo.status !== 'pending' ? (
                <span className="bg-green-100 text-green-700 text-xs font-semibold px-2 py-1 rounded-lg">
                  Payment Verified
                </span>
              ) : (
                <span className="bg-red-100 text-red-700 text-xs font-semibold px-2 py-1 rounded-lg">
                  Payment Not Verified
                </span>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Job Details Modal */}
      <Dialog open={showJobDetails} onOpenChange={setShowJobDetails}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-center text-green-600">
              Matched!
            </DialogTitle>
            <DialogDescription className="text-center text-lg">
              {job.isRequestedForVolunteer ? (
                <span className="text-orange-600 font-medium">
                  This is a volunteer opportunity. You will not be paid for this service.
                </span>
              ) : (
                `You've been matched with ${job.houseOwnerId.userName}'s job request`
              )}
            </DialogDescription>
          </DialogHeader>

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md">
              {error}
            </div>
          )}

          <div className="max-h-[60vh] overflow-y-auto">
            <JobInformation jobData={job} />
          </div>

          <DialogFooter className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={handleBack}
              disabled={loading}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </Button>
            <Button
              onClick={handleAcceptJob}
              disabled={loading}
              className="flex items-center gap-2 bg-green-600 hover:bg-green-700"
            >
              {loading ? (
                <Loader size="w-4 h-4" fScreen='' />
              ) : (
                <CheckCircle className="w-4 h-4" />
              )}
              Accept Job
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}